/* eslint-disable no-undef */
import { API } from './requests';
import {
	CUSTOM_DATASET_ID,
	DAILY,
	WEEKLY,
	MONTHLY,
	QUARTERLY,
	LANDINGS,
	BASIC,
	CHARTER,
	ABC,
	LATER,
	WEEK_KEY,
	CUSTOM,
	MONTH_KEY,
	QUARTER_KEY,
	YEAR_KEY,
	FILTER_CONDITION_LABELS,
	LAST_WEEKEND_KEY,
	CUSTOM_DATE_1,
	CUSTOM_DATE_2,
	DAY_KEY_LOWERCASE
} from 'constants/constant';
import moment from 'moment';
import { deepClone } from 'helper/helper';
import { STR_TMPT_LIST, INITIAL_SSI_COMPATIBILITY, IS_BEFORE_52_WEEKS } from 'constants/constant';
import { wmtLastOneHundredAndFourWeeks } from 'components/Preview/filter/FilterCondition';
import { getAPIURL } from 'helper/helper';
import { FUZZY_OPTIONS } from 'constants/constant';
import { YEAR_TO_DATE_KEY } from 'constants/constant';
import { WALMART_CALENDER_KEY } from 'constants/constant';

const apiURL = getAPIURL();

export const isServer = !(typeof window !== 'undefined' && window.document);

export const getCurrentEnv = () => {
	const env = 'prod';

	if (!isServer) {
		const { host } = window.location;
		if (host.includes('dev.') || host.includes('localhost')) {
			return 'dev';
		}

		if (host.includes('stg.') || host.includes('stage.')) {
			return 'stage';
		}

		if (host.includes('preprod.')) {
			return 'preprod';
		}
	}

	return env;
};

export const getRunningEnv = () =>
	`${
		typeof process !== 'undefined' && process.env?.REACT_APP_ENV_HOST
			? process.env.REACT_APP_ENV_HOST
			: getCurrentEnv()
	}`;

export const routeToBaseApp = () => {
	window.location.href = `${window?.location?.protocol}//${window.location.host}`;
};

export const routeToApps = () => {
	window.location.href = `${window?.location?.protocol}//${window.location.host}/apps`;
};
export const getRedirectUrl = (currentUrl = '') => {
	const landings = {
		prod: apiURL,
		preprod: apiURL,
		stage: apiURL,
		dev: apiURL
	};

	const variables = {
		prod: `${landings?.prod}/login?lastUrl=${landings?.prod}${currentUrl}`,
		preprod: `${landings?.preprod}/login?lastUrl=${landings?.preprod}${currentUrl}`,
		stage: `${landings?.stage}/login?lastUrl=${landings?.stage}${currentUrl}`,
		dev: `${landings?.dev}/login?lastUrl=${landings?.dev}${currentUrl}`
	};
	return variables[getRunningEnv()];
};

export const getBaseUrl = () => {
	const variables = {
		prod: `${LANDINGS?.prod}`,
		preprod: `${LANDINGS?.preprod}`,
		stage: `${LANDINGS?.stage}`,
		dev: `${LANDINGS?.dev}`
	};
	return variables[getRunningEnv()];
};

export const routeToLogin = () => {
	window.location.href = getRedirectUrl(window?.location?.pathname);
};

export const checkStatus = (resCode) => {
	if (
		[API.RESPONSE.FORBIDDEN, API.RESPONSE.REDIRECTSTATUS, API.RESPONSE.UNAUTHORIZED].includes(
			resCode
		)
	) {
		localStorage.removeItem('selectedSubMenuKey');
		localStorage.removeItem('selectedMainMenuKey');
		routeToLogin();
	}
};

export const getDateTime = (customerType = CHARTER) => {
	const today = new Date();
	let date = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
	const time = '03:00:00'; // default time is 3 AM for fixed deliver time
	if (customerType === BASIC) {
		date = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate() + 1}`;
	}

	return { datetime: `${date} ${time}`, date: today.getDate() };
};

export function disableMonth(date, customerType) {
	const today = new Date();
	const checkDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

	if (customerType === BASIC && checkDate.getTime() === todayDate.getTime()) {
		return true;
	}

	todayDate.setMonth(todayDate.getMonth() + 1);

	if (checkDate > todayDate) {
		return true;
	}
}

export const datePrefix = (value) => {
	// Handle the special case for "L"
	if (value === 'L') {
		return 'Last day ';
	}

	// Determine the suffix based on the numeric value
	switch (Number(value)) {
		case 1:
		case 21:
		case 31:
			return 'st';
		case 2:
		case 22:
			return 'nd';
		case 3:
		case 23:
			return 'rd';
		default:
			return 'th';
	}
};

export const dateToCron = (param) => {
	const { cronType, selectedDateTime, weeks, quarter, selectedDate, isDynamicSelected } = param;
	const date = new Date(selectedDateTime);
	// In the case of Dynamic scheduling, the cron should run at 12 AM
	// Expression '0 0 * * *' represents 12 AM
	const hour = isDynamicSelected ? 0 : date.getHours() || 0;
	let month;

	const quarters = () => {
		switch (quarter) {
			case 2:
				return '2,5,8,11';
			case 3:
				return '3,6,9,12';
			default:
				return '1,4,7,10';
		}
	};

	if (cronType === QUARTERLY) {
		month = quarters();
	} else {
		month = date.getMonth() + 1;
	}

	const finalDate = [29, 30, 31].includes(selectedDate) ? 'L' : selectedDate;

	switch (cronType) {
		case DAILY:
			return `0 ${hour} * * *`;
		case WEEKLY:
			return `0 ${hour} * * ${weeks}`;
		case MONTHLY:
			return `0 ${hour} ${finalDate} * *`;
		case QUARTERLY:
			return `0 ${hour} ${finalDate} ${month} *`;
		default:
			return '@once';
	}
};

const getWeekType = (week) => {
	if (week[0] === 'mon-fri') {
		return 'Weekdays';
	} else if (week[0] === 'sat' && week[1] === 'sun' && week.length === 2) {
		return 'Weekends';
	} else {
		return 'Custom';
	}
};

export const cronToDate = (cron) => {
	const fetchFromCron = cron?.split(' ');
	if (fetchFromCron !== undefined) {
		const hour = fetchFromCron[1] === '*' ? 6 : fetchFromCron[1];
		const date = fetchFromCron[2] === '*' ? 1 : fetchFromCron[2];
		const month = fetchFromCron[3] === '*' ? 1 : fetchFromCron[3];
		const week = fetchFromCron[4]?.split(',');
		const weekSelected = week ? getWeekType(week) : undefined;
		const setQuarter = () => {
			switch (month) {
				case '2,5,8,11':
					return 2;
				case '3,6,9,12':
					return 3;
				default:
					return 1;
			}
		};
		return { hour, date, month, week, weekSelected, quarter: setQuarter() };
	}
	return;
};

export const getDatetimeFromCron = (hour, date, month, type, scheduleStartDate) => {
	const today = new Date();
	let dt;
	let hr = hour;
	if (type === QUARTERLY) {
		dt = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
	} else if (type === LATER) {
		dt = moment.utc(scheduleStartDate).local().format('YYYY-MM-DD');
		hr = moment.utc(scheduleStartDate).local().format('HH');
	} else {
		if (month === '*' && date === '*') {
			dt = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
		} else if (month === '*' && date !== '*') {
			dt = `${today.getFullYear()}-${today.getMonth() + 1}-${date}`;
		} else if (month !== '*') {
			dt = `${today.getFullYear()}-${month}-${today.getDate()}`;
		} else {
			dt = `${today.getFullYear()}-${month}-${date}`;
		}
	}
	const time = `${hr}:00:00`;
	return { datetime: `${dt} ${time}`, date: date === 'L' ? 31 : date, time: hr };
};

export const getWeeks = (param) => {
	const { weekType, weeks = null } = param;
	const weekList = weeks && String(Object.keys(weeks).filter((item) => weeks[item] === true));
	switch (weekType) {
		case 'Weekdays':
			return 'mon-fri';
		case 'Weekends':
			return 'sat,sun';
		case 'Custom':
			return weekList.toLowerCase();
		default:
			return '';
	}
};

const getFileName = (fileUrl) => {
	const splitData = fileUrl.split('/');
	return splitData[splitData.length - 1];
};

export const download = (url, fileUrl) => {
	const a = document.createElement('a');
	a.style.display = 'none';
	a.href = url;
	a.download = getFileName(fileUrl);
	document.body.appendChild(a);
	a.click();
	document.body.removeChild(a);
	window.URL.revokeObjectURL(url);
};

export const getSchedule = (cronExp, frequency, scheduleStartDate) => {
	const cron = cronExp?.split(' ');
	if (cron) {
		const time = cron[1];
		const date = cron[2];
		const month = cron[3];
		const week = cron[4];
		const getTime = () => {
			if (time == 12) {
				return `12:00`;
			}
			return `${time}:00`;
		};
		const getQuarters = () => {
			switch (month) {
				case '2,5,8,11':
					return '2nd';
				case '3,6,9,12':
					return '3rd';
				default:
					return '1st';
			}
		};
		const quarter = getQuarters();
		const isNotCommaDelimiter = week?.split('-').length === 2;
		const days = week?.split(',').join('.').split('-').join('.').split('.');
		const capitalizedWeek = days
			?.map((day) => day.charAt(0).toUpperCase() + day.slice(1).toLowerCase())
			.join(isNotCommaDelimiter ? '-' : ', ');
		const fetchSchedule = () => {
			switch (frequency) {
				case DAILY:
					return `${DAILY} | ${getTime()}`;
				case WEEKLY:
					return `${WEEKLY} | ${getTime()}, every ${capitalizedWeek}`;
				case MONTHLY:
					if (date <= 27) {
						return `${MONTHLY} | ${getTime()}, ${date}${datePrefix(date)} of every month`;
					} else {
						return `${MONTHLY} | ${getTime()}, ${datePrefix(date)} of every month`;
					}
				case QUARTERLY:
					if (date !== 'L') {
						return `${QUARTERLY} | ${getTime()}, ${date}${datePrefix(
							date
						)} of ${quarter} month of each quarter`;
					} else {
						return `${QUARTERLY} | ${getTime()}, ${datePrefix(
							date
						)} of ${quarter} month of each quarter`;
					}
				default:
					return moment.utc(scheduleStartDate).local().format('MM/DD/YYYY HH:mm');
			}
		};

		return fetchSchedule();
	}
};

export const getEndDate = (val, scheduleType) => {
	const date = new Date();
	let updateDate = moment(date, 'MM/DD/YYYY');

	switch (scheduleType) {
		case DAILY:
			updateDate = updateDate.add(val, 'days');
			break;
		case WEEKLY:
			updateDate = updateDate.add(val, 'weeks');
			break;
		case MONTHLY:
			updateDate = updateDate.add(val, 'month');
			break;
		case QUARTERLY:
			updateDate = updateDate.add(val * 3, 'month');
			break;
		default:
			return undefined;
	}

	return moment.utc(updateDate).local().format('MM/DD/YYYY');
};

export const formatBytes = (bytes, decimals = 2) => {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB', 'GB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

export const getLastRun = (lastRun) => {
	if (lastRun) {
		const today = moment(new Date()).format('MM/DD/YYYY');
		const date = moment.utc(lastRun).local().format('MM/DD/YYYY');
		if (today === date) {
			return moment.utc(lastRun).local().format('HH:mm');
		}
		return date;
	}
};

export const getDfltFilter = (result, dfltFiltersOnDimensions, timeRequired = true) => {
	const defaultFilterSel = [];
	let value;
	if (timeRequired && result[0]?.time) {
		const parent = result[0]?.time[0]?.businessName;
		value = result[0]?.time[0]?.subAttributes.find((el) => el.businessName === 'Week');
		if (parent === '') {
			value = result[0]?.time[0]?.subAttributes.find((el) => el.businessName === 'PO Create Date');
		}

		defaultFilterSel.push({
			...value,
			parent: parent,
			filter: { condition: '', value: [] }
		});
	}

	if (dfltFiltersOnDimensions?.length > 0) {
		dfltFiltersOnDimensions?.map((dim) =>
			defaultFilterSel.push({
				...dim.dimension,
				parent: 'dimensions',
				filter: {
					condition: dim.condition,
					value: dim.value
				}
			})
		);
	}
	return defaultFilterSel;
};

export const getView = (isMultiWeekSel, isWkSel, selectedTemplate) => {
	const lTmpltId = Number(selectedTemplate);
	// if it is a custom template, then we assign it to "abc" which is a dummy string
	if (isNaN(lTmpltId) || lTmpltId === -1) {
		return ABC;
	}
	let val;
	switch (lTmpltId) {
		case 401:
			if (isMultiWeekSel) {
				val = 'OMNI_SALES_MULTI_WINDOW';
			} else {
				val = 'OMNI_SALES';
			}
			break;
		case 402:
			if (isMultiWeekSel) {
				val = 'OMNI_SALES_CATMAN_MULTI_WINDOW';
			} else {
				val = 'OMNI_SALES_CATMAN';
			}
			break;
		case 403:
			if (isMultiWeekSel) {
				val = 'STORE_DETAILS_WEEKLY_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'STORE_DETAILS_WEEKLY';
			} else {
				val = 'STORE_DETAILS_DAILY';
			}
			break;
		case 404:
			if (isMultiWeekSel) {
				val = 'STORE_DETAILS_WEEKLY_CATMAN_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'STORE_DETAILS_WEEKLY_CATMAN';
			} else {
				val = 'STORE_DETAILS_DAILY_CATMAN';
			}
			break;
		case 405:
			if (isMultiWeekSel) {
				val = 'DC_INVENTORY_SALES_WEEKLY_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'DC_INVENTORY_SALES_WEEKLY';
			} else {
				val = 'DC_INVENTORY_SALES_DAILY';
			}
			break;
		case 406:
			if (isMultiWeekSel) {
				val = 'DC_INVENTORY_SALES_WEEKLY_CATMAN_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'DC_INVENTORY_SALES_WEEKLY_CATMAN';
			} else {
				val = 'DC_INVENTORY_SALES_DAILY_CATMAN';
			}
			break;
		case 414:
			if (isMultiWeekSel) {
				val = 'OMNI_OTIF_SUPPLIER_MULTI_WINDOW';
			} else {
				val = 'OMNI_OTIF_SUPPLIER';
			}
			break;
		case 415:
			if (isMultiWeekSel) {
				val = 'ECOM_INVENTORY_SUPPLIER_DAILY_MULTI_WINDOW';
			} else {
				val = 'ECOM_INVENTORY_SUPPLIER_DAILY';
			}
			break;
		case 416:
			if (isMultiWeekSel) {
				val = 'ECOM_INVENTORY_CATMAN_DAILY_MULTI_WINDOW';
			} else {
				val = 'ECOM_INVENTORY_CATMAN_DAILY';
			}
			break;
		case 408:
			val = 'MODULAR_PLAN_SUPPLIER';
			break;
		case 409:
			val = 'MODULAR_PLAN_CATMAN';
			break;
		case 502:
			if (isMultiWeekSel) {
				val = 'MERCHANT_OMNI_CATMAN_MULTI_WINDOW';
			} else {
				val = 'MERCHANT_OMNI_CATMAN';
			}
			break;
		case 504:
			if (isMultiWeekSel) {
				val = 'MERCHANT_STORE_CATMAN_WEEKLY_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'MERCHANT_STORE_CATMAN_WEEKLY';
			} else {
				val = 'MERCHANT_STORE_CATMAN_DAILY';
			}
			break;
		case 604:
			if (isMultiWeekSel) {
				val = 'BASIC_STORE_DETAILS_WEEKLY_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'BASIC_CATMAN_STORE_DETAILS_WEEKLY';
			} else {
				val = 'BASIC_CATMAN_STORE_DETAILS_DAILY';
			}
			break;
		case 603:
			if (isMultiWeekSel) {
				val = 'BASIC_STORE_DETAILS_WEEKLY_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'BASIC_STORE_DETAILS_WEEKLY';
			} else {
				val = 'BASIC_STORE_DETAILS_DAILY';
			}
			break;
		case 506:
			if (isMultiWeekSel) {
				val = 'MERCHANT_DC_INVENTORY_CATMAN_WEEKLY_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'MERCHANT_DC_INVENTORY_CATMAN_WEEKLY';
			} else {
				val = 'MERCHANT_DC_INVENTORY_CATMAN_DAILY';
			}
			break;
		case 606:
			if (isMultiWeekSel) {
				val = 'BASIC_DC_INVENTORY_SALES_WEEKLY_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'BASIC_CATMAN_DC_INVENTORY_SALES_WEEKLY';
			} else {
				val = 'BASIC_CATMAN_DC_INVENTORY_SALES_DAILY';
			}
			break;
		case 605:
			if (isMultiWeekSel) {
				val = 'BASIC_DC_INVENTORY_SALES_WEEKLY_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'BASIC_DC_INVENTORY_SALES_WEEKLY';
			} else {
				val = 'BASIC_DC_INVENTORY_SALES_DAILY';
			}
			break;
		case 608:
			val = 'BASIC_MODULAR_PLAN_SUPPLIER';
			break;
		case 609:
			val = 'BASIC_MODULAR_PLAN_CATMAN';
			break;
		case 509:
			val = 'MERCHANT_MODULAR_CATMAN';
			break;
		case 410:
			val = 'STORE_ORDER_FORECAST';
			break;
		case 411:
			val = 'STORE_ORDER_FORECAST_CATMAN';
			break;
		case 611:
			val = 'BASIC_CATMAN_STORE_ORDER_FORECAST';
			break;
		case 613:
			val = 'BASIC_CATMAN_STORE_DEMAND_FORECAST';
			break;
		case 610:
			val = 'BASIC_STORE_ORDER_FORECAST';
			break;
		case 412:
			val = 'STORE_DEMAND_FORECAST';
			break;
		case 413:
			val = 'STORE_DEMAND_FORECAST_CATMAN';
			break;
		case 617:
			if (isMultiWeekSel) {
				val = 'BASIC_STORE_MARKUP_AND_MARKDOWNS_MULTI_WINDOW';
			} else {
				val = 'BASIC_STORE_MARKUP_AND_MARKDOWNS';
			}
			break;
		case 618:
			if (isMultiWeekSel) {
				val = 'BASIC_STORE_MARKUP_AND_MARKDOWNS_MULTI_WINDOW';
			} else {
				val = 'BASIC_CATMAN_STORE_MARKUP_AND_MARKDOWNS';
			}
			break;
		case 612:
			val = 'BASIC_STORE_DEMAND_FORECAST';
			break;
		case 510:
			val = 'STORE_ORDER_FORECAST_MERCHANT';
			break;
		case 511:
			val = 'STORE_DEMAND_FORECAST_MERCHANT';
			break;
		case 417:
			if (isMultiWeekSel) {
				val = 'STORE_MARKUP_AND_MARKDOWNS_MULTI_WINDOW';
			} else {
				val = 'STORE_MARKUP_AND_MARKDOWNS';
			}
			break;
		case 418:
			if (isMultiWeekSel) {
				val = 'STORE_MARKUP_AND_MARKDOWNS_CATMAN_MULTI_WINDOW';
			} else {
				val = 'STORE_MARKUP_AND_MARKDOWNS_CATMAN';
			}
			break;
		case 517:
			if (isMultiWeekSel) {
				val = 'MERCHANT_STORE_MARKUP_AND_MARKDOWNS_MULTI_WINDOW';
			} else {
				val = 'MERCHANT_STORE_MARKUP_AND_MARKDOWNS';
			}
			break;
		case 514:
			if (isMultiWeekSel) {
				val = 'OMNI_OTIF_MERCHANT_MULTI_WINDOW';
			} else {
				val = 'OMNI_OTIF_MERCHANT';
			}
			break;
		case 701:
			if (isMultiWeekSel) {
				val = 'BASIC_ECOMM_SALES_MULTI_WINDOW';
			} else {
				val = 'BASIC_ECOMM_SALES';
			}
			break;
		case 702:
			if (isMultiWeekSel) {
				val = 'BASIC_ECOMM_SALES_MULTI_WINDOW';
			} else {
				val = 'BASIC_CATMAN_ECOMM_SALES';
			}
			break;
		case 515:
			if (isMultiWeekSel) {
				val = 'MERCHANT_ECOM_INVENTORY_DAILY_MULTI_WINDOW';
			} else {
				val = 'MERCHANT_ECOM_INVENTORY_DAILY';
			}
			break;
		case 801:
			val = 'VENDOR_SCORECARD';
			break;
		case 802:
			val = 'VENDOR_SCORECARD_CATMAN';
			break;
		case 901:
			val = 'MERCHANT_VENDOR_SCORECARD';
			break;
		case 951:
			val = 'BASIC_VENDOR_SCORECARD';
			break;
		case 952:
			val = 'BASIC_CATMAN_VENDOR_SCORECARD';
			break;
		case 1001:
			val = 'MERCHANT_VENDOR_SCORECARD_2024';
			break;
		case 1002:
			val = 'VENDOR_SCORECARD_2024';
			break;
		case 1003:
			val = 'BASIC_VENDOR_SCORECARD_2024';
			break;
		case 1004:
			val = 'VENDOR_SCORECARD_2025';
			break;
		case 1005:
			val = 'VENDOR_SCORECARD_CATMAN2025';
			break;
		case 1006:
			val = 'MERCHANT_VENDOR_SCORECARD_2025';
			break;
		case 1101:
			val = 'FUTURE_VALID_ITEM_STORE';
			break;
		case 1102:
			val = 'FUTURE_VALID_ITEM_STORE_CATMAN';
			break;
		case 1201:
			val = 'FUTURE_VALID_ITEM_STORE_MERCHANT';
			break;
		case 1151:
			val = 'BASIC_FUTURE_VALID_ITEM_STORE';
			break;
		case 1152:
			val = 'BASIC_CATMAN_FUTURE_VALID_ITEM_STORE';
			break;
		case 1301:
			val = 'TENDER_ANALYSIS';
			break;
		case 1302:
			val = 'TENDER_ANALYSIS_CATMAN';
			break;
		case 1351:
			val = 'BASIC_TENDER_ANALYSIS';
			break;
		case 1352:
			val = 'BASIC_CATMAN_TENDER_ANALYSIS';
			break;
		case 1401:
			val = 'TENDER_ANALYSIS_MERCHANT';
			break;
		case 1501:
			val = 'ECOM_BOTTLE_DEPOSITS';
			break;
		case 1502:
			val = 'ECOM_BOTTLE_DEPOSITS_CATMAN';
			break;
		case 1551:
			val = 'ECOM_BOTTLE_DEPOSITS_MERCHANT';
			break;
		case 1701:
			val = 'ECOM_INSTOCK_SUPPLIER';
			break;
		case 1702:
			val = 'ECOM_INSTOCK_CATMAN';
			break;
		case 1751:
			val = 'BASIC_ECOM_INSTOCK';
			break;
		case 1752:
			val = 'BASIC_CATMAN_ECOM_INSTOCK';
			break;
		case 1801:
			val = 'ECOM_INSTOCK_MERCHANT';
			break;
		case 1703:
			val = 'QUICK_ITEM_INFO_SUPPLIER';
			break;
		case 1704:
			val = 'QUICK_ITEM_INFO_CATMAN';
			break;
		case 1753:
			val = 'BASIC_QUICK_ITEM_INFO';
			break;
		case 1754:
			val = 'BASIC_CATMAN_QUICK_ITEM_INFO';
			break;
		case 1802:
			val = 'QUICK_ITEM_INFO_MERCHANT';
			break;
		case 615:
			if (isMultiWeekSel) {
				val = 'BASIC_ECOM_INVENTORY_MULTI_WINDOW';
			} else {
				val = 'BASIC_ECOM_INVENTORY';
			}
			break;
		case 616:
			if (isMultiWeekSel) {
				val = 'BASIC_ECOM_INVENTORY_MULTI_WINDOW';
			} else {
				val = 'BASIC_CATMAN_ECOM_INVENTORY';
			}
			break;

		case 421:
			if (isMultiWeekSel) {
				val = 'REGISTER_SALES_WEEKLY_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'REGISTER_SALES_WEEKLY';
			} else {
				val = 'REGISTER_SALES_DAILY';
			}
			break;
		case 422:
			if (isMultiWeekSel) {
				val = 'REGISTER_SALES_WEEKLY_CATMAN_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'REGISTER_SALES_WEEKLY_CATMAN';
			} else {
				val = 'REGISTER_SALES_DAILY_CATMAN';
			}
			break;
		case 521:
			if (isMultiWeekSel) {
				val = 'REGISTER_SALES_WEEKLY_MERCHANT_MULTI_WINDOW';
			} else if (isWkSel) {
				val = 'REGISTER_SALES_WEEKLY_MERCHANT';
			} else {
				val = 'REGISTER_SALES_DAILY_MERCHANT';
			}
			break;
		case 419:
			val = 'ENTERPRISE_INVENTORY_SUPPLIER';
			break;
		case 519:
			val = 'ENTERPRISE_INVENTORY_MERCHANT';
			break;
		case 420:
			if (isWkSel) {
				val = 'PURCHASE_ORDER_WEEKLY';
			} else {
				val = 'PURCHASE_ORDER_DAILY';
			}
			break;
		case 520:
			if (isWkSel) {
				val = 'PURCHASE_ORDER_WEEKLY_MERCHANT';
			} else {
				val = 'PURCHASE_ORDER_DAILY_MERCHANT';
			}
			break;
		case 423:
			if (isWkSel) {
				val = 'DECADE_OMNI_SALES_WEEKLY';
			} else {
				val = 'DECADE_OMNI_SALES_DAILY';
			}
			break;
		case 424:
			if (isWkSel) {
				val = 'DECADE_OMNI_SALES_CATMAN_WEEKLY';
			} else {
				val = 'DECADE_OMNI_SALES_CATMAN_DAILY';
			}
			break;
		case 524:
			if (isWkSel) {
				val = 'DECADE_OMNI_SALES_WEEKLY_MERCHANT';
			} else {
				val = 'DECADE_OMNI_SALES_DAILY_MERCHANT';
			}
			break;
	}
	return val;
};

export const getTemplateId = (view) => {
	let val = '401';
	switch (view) {
		case 'OMNI_SALES':
		case 'OMNI_SALES_MULTI_WINDOW':
			val = 401;
			break;
		case 'OMNI_SALES_CATMAN':
		case 'OMNI_SALES_CATMAN_MULTI_WINDOW':
			val = 402;
			break;
		case 'STORE_DETAILS_WEEKLY':
		case 'STORE_DETAILS_DAILY':
		case 'STORE_DETAILS_WEEKLY_MULTI_WINDOW':
			val = 403;
			break;
		case 'STORE_DETAILS_WEEKLY_CATMAN':
		case 'STORE_DETAILS_DAILY_CATMAN':
		case 'STORE_DETAILS_WEEKLY_CATMAN_MULTI_WINDOW':
			val = 404;
			break;
		case 'BASIC_CATMAN_STORE_DETAILS_WEEKLY':
		case 'BASIC_CATMAN_STORE_DETAILS_DAILY':
			val = 604;
			break;
		case 'BASIC_STORE_DETAILS_WEEKLY':
		case 'BASIC_STORE_DETAILS_DAILY':
		case 'BASIC_STORE_DETAILS_WEEKLY_MULTI_WINDOW':
			val = 603;
			break;
		case 'DC_INVENTORY_SALES_WEEKLY':
		case 'DC_INVENTORY_SALES_DAILY':
		case 'DC_INVENTORY_SALES_WEEKLY_MULTI_WINDOW':
			val = 405;
			break;
		case 'DC_INVENTORY_SALES_WEEKLY_CATMAN':
		case 'DC_INVENTORY_SALES_DAILY_CATMAN':
		case 'DC_INVENTORY_SALES_WEEKLY_CATMAN_MULTI_WINDOW':
			val = 406;
			break;
		case 'OMNI_OTIF_SUPPLIER':
		case 'OMNI_OTIF_SUPPLIER_MULTI_WINDOW':
			val = 414;
			break;
		case 'MERCHANT_OMNI_CATMAN':
		case 'MERCHANT_OMNI_CATMAN_MULTI_WINDOW':
			val = 502;
			break;
		case 'MERCHANT_STORE_CATMAN_DAILY':
		case 'MERCHANT_STORE_CATMAN_WEEKLY':
		case 'MERCHANT_STORE_CATMAN_WEEKLY_MULTI_WINDOW':
			val = 504;
			break;
		case 'MERCHANT_DC_INVENTORY_CATMAN_DAILY':
		case 'MERCHANT_DC_INVENTORY_CATMAN_WEEKLY':
		case 'MERCHANT_DC_INVENTORY_CATMAN_WEEKLY_MULTI_WINDOW':
			val = 506;
			break;
		case 'BASIC_CATMAN_DC_INVENTORY_SALES_WEEKLY':
		case 'BASIC_CATMAN_DC_INVENTORY_SALES_DAILY':
			val = 606;
			break;
		case 'BASIC_MODULAR_PLAN_SUPPLIER':
			val = 608;
			break;
		case 'BASIC_MODULAR_PLAN_CATMAN':
			val = 609;
			break;
		case 'BASIC_CATMAN_STORE_DEMAND_FORECAST':
			val = 613;
			break;
		case 'BASIC_DC_INVENTORY_SALES_WEEKLY':
		case 'BASIC_DC_INVENTORY_SALES_DAILY':
		case 'BASIC_DC_INVENTORY_SALES_WEEKLY_MULTI_WINDOW':
			val = 605;
			break;
		case 'OMNI_OTIF_MERCHANT':
		case 'OMNI_OTIF_MERCHANT_MULTI_WINDOW':
			val = 514;
			break;
		case 'MERCHANT_MODULAR_CATMAN':
			val = 509;
			break;
		case 'STORE_ORDER_FORECAST':
			val = 410;
			break;
		case 'STORE_ORDER_FORECAST_CATMAN':
			val = 411;
			break;
		case 'BASIC_CATMAN_STORE_ORDER_FORECAST':
			val = 611;
			break;
		case 'BASIC_STORE_ORDER_FORECAST':
			val = 610;
			break;
		case 'STORE_DEMAND_FORECAST':
			val = 412;
			break;
		case 'STORE_DEMAND_FORECAST_CATMAN':
			val = 413;
			break;
		case 'BASIC_STORE_MARKUP_AND_MARKDOWNS':
		case 'BASIC_STORE_MARKUP_AND_MARKDOWNS_MULTI_WINDOW':
			val = 617;
			break;
		case 'BASIC_CATMAN_STORE_MARKUP_AND_MARKDOWNS':
			val = 618;
			break;
		case 'BASIC_STORE_DEMAND_FORECAST':
			val = 612;
			break;
		case 'STORE_ORDER_FORECAST_MERCHANT':
			val = 510;
			break;
		case 'STORE_DEMAND_FORECAST_MERCHANT':
			val = 511;
			break;
		case 'VENDOR_SCORECARD':
			val = 801;
			break;
		case 'VENDOR_SCORECARD_CATMAN':
			val = 802;
			break;
		case 'BASIC_VENDOR_SCORECARD':
			val = 951;
			break;
		case 'BASIC_CATMAN_VENDOR_SCORECARD':
			val = 952;
			break;
		case 'MERCHANT_VENDOR_SCORECARD_2024':
			val = 1001;
			break;
		case 'VENDOR_SCORECARD_2024':
			val = 1002;
			break;
		case 'BASIC_VENDOR_SCORECARD_2024':
			val = 1003;
			break;
		case 'VENDOR_SCORECARD_2025':
			val = 1004;
			break;
		case 'VENDOR_SCORECARD_CATMAN2025':
			val = 1005;
			break;
		case 'MERCHANT_VENDOR_SCORECARD_2025':
			val = 1006;
			break;
		case 'FUTURE_VALID_ITEM_STORE':
			val = 1101;
			break;
		case 'FUTURE_VALID_ITEM_STORE_CATMAN':
			val = 1102;
			break;
		case 'FUTURE_VALID_ITEM_STORE_MERCHANT':
			val = 1201;
			break;
		case 'BASIC_FUTURE_VALID_ITEM_STORE':
			val = 1151;
			break;
		case 'BASIC_CATMAN_FUTURE_VALID_ITEM_STORE':
			val = 1152;
			break;
		case 'TENDER_ANALYSIS':
			val = 1301;
			break;
		case 'TENDER_ANALYSIS_CATMAN':
			val = 1302;
			break;
		case 'BASIC_TENDER_ANALYSIS':
			val = 1351;
			break;
		case 'BASIC_CATMAN_TENDER_ANALYSIS':
			val = 1352;
			break;
		case 'TENDER_ANALYSIS_MERCHANT':
			val = 1401;
			break;
		case 'ECOM_INSTOCK_SUPPLIER':
			val = 1701;
			break;
		case 'ECOM_INSTOCK_CATMAN':
			val = 1702;
			break;
		case 'ECOM_INSTOCK_MERCHANT':
			val = 1801;
			break;
		case 'BASIC_ECOM_INSTOCK':
			val = 1751;
			break;
		case 'BASIC_CATMAN_ECOM_INSTOCK':
			val = 1752;
			break;
		case 'ECOM_BOTTLE_DEPOSITS':
			val = 1501;
			break;
		case 'ECOM_BOTTLE_DEPOSITS_CATMAN':
			val = 1502;
			break;
		case 'ECOM_BOTTLE_DEPOSITS_MERCHANT':
			val = 1551;
			break;
		case 'QUICK_ITEM_INFO_SUPPLIER':
			val = 1703;
			break;
		case 'QUICK_ITEM_INFO_CATMAN':
			val = 1704;
			break;
		case 'BASIC_QUICK_ITEM_INFO':
			val = 1753;
			break;
		case 'BASIC_CATMAN_QUICK_ITEM_INFO':
			val = 1754;
			break;
		case 'QUICK_ITEM_INFO_MERCHANT':
			val = 1802;
			break;
		case 'BASIC_ECOMM_SALES':
		case 'BASIC_ECOMM_SALES_MULTI_WINDOW':
			val = 701;
			break;
		case 'BASIC_CATMAN_ECOMM_SALES':
			val = 702;
			break;
		case 'MERCHANT_VENDOR_SCORECARD':
			val = 901;
			break;
		case 'BASIC_ECOM_INVENTORY':
		case 'BASIC_ECOM_INVENTORY_MULTI_WINDOW':
			val = 615;
			break;
		case 'BASIC_CATMAN_ECOM_INVENTORY':
			val = 616;
			break;
		case 'ECOM_INVENTORY_SUPPLIER_DAILY':
		case 'ECOM_INVENTORY_SUPPLIER_DAILY_MULTI_WINDOW':
			val = 415;
			break;
		case 'ECOM_INVENTORY_CATMAN_DAILY':
		case 'ECOM_INVENTORY_CATMAN_DAILY_MULTI_WINDOW':
			val = 416;
			break;
		case 'MERCHANT_ECOM_INVENTORY_DAILY':
		case 'MERCHANT_ECOM_INVENTORY_DAILY_MULTI_WINDOW':
			val = 515;
			break;
		case 'STORE_MARKUP_AND_MARKDOWNS':
		case 'STORE_MARKUP_AND_MARKDOWNS_MULTI_WINDOW':
			val = 417;
			break;
		case 'STORE_MARKUP_AND_MARKDOWNS_CATMAN':
		case 'STORE_MARKUP_AND_MARKDOWNS_CATMAN_MULTI_WINDOW':
			val = 418;
			break;
		case 'MERCHANT_STORE_MARKUP_AND_MARKDOWNS':
		case 'MERCHANT_STORE_MARKUP_AND_MARKDOWNS_MULTI_WINDOW':
			val = 517;
			break;
		case 'ENTERPRISE_INVENTORY_SUPPLIER':
			val = 419;
			break;
		case 'ENTERPRISE_INVENTORY_MERCHANT':
			val = 519;
			break;
		case 'PURCHASE_ORDER_SUPPLIER_WEEKLY':
		case 'PURCHASE_ORDER_SUPPLIER':
		case 'PURCHASE_ORDER_DAILY':
			val = 420;
			break;
		case 'PURCHASE_ORDER_MERCHANT_WEEKLY':
		case 'PURCHASE_ORDER_MERCHANT':
		case 'PURCHASE_ORDER_DAILY_MERCHANT':
			val = 520;
			break;
		case 'REGISTER_SALES_DAILY':
		case 'REGISTER_SALES_WEEKLY':
			val = 421;
			break;
		case 'REGISTER_SALES_DAILY_CATMAN':
		case 'REGISTER_SALES_WEEKLY_CATMAN':
			val = 422;
			break;
		case 'REGISTER_SALES_DAILY_MERCHANT':
		case 'REGISTER_SALES_WEEKLY_MERCHANT':
			val = 521;
			break;
		case 'DECADE_OMNI_SALES_DAILY':
		case 'DECADE_OMNI_SALES_WEEKLY':
			val = 423;
			break;
		case 'DECADE_OMNI_SALES_CATMAN_DAILY':
		case 'DECADE_OMNI_SALES_CATMAN_WEEKLY':
			val = 424;
			break;
		case 'DECADE_OMNI_SALES_DAILY_MERCHANT':
		case 'DECADE_OMNI_SALES_WEEKLY_MERCHANT':
			val = 524;
			break;
	}
	return val;
};

export const getTmplName = (selectedTemplate) => {
	const lTmpltId = Number(selectedTemplate);
	// if it is a custom template, then we assign it to "abc" which is a dummy string
	if (isNaN(lTmpltId) || lTmpltId === -1) {
		return selectedTemplate;
	}
	let val;
	switch (lTmpltId) {
		case 420:
		case 520:
		case 620:
			val = 'Purchase Order';
			break;
		case 401:
		case 402:
		case 502:
		case 602:
		case 601:
			val = 'Omni Sales';
			break;
		case 403:
		case 404:
		case 504:
		case 604:
		case 603:
			val = 'Store Sales & Inventory';
			break;
		case 405:
		case 406:
		case 506:
		case 606:
		case 605:
			val = 'DC Metrics';
			break;
		case 414:
		case 514:
		case 614:
			val = 'OMNI OTIF';
			break;
		case 408:
		case 409:
		case 509:
		case 608:
		case 609:
			val = 'Modular Plan Metrics';
			break;
		case 510:
		case 411:
		case 611:
		case 610:
		case 410:
			val = 'Order Forecast';
			break;
		case 511:
		case 413:
		case 412:
		case 613:
		case 612:
			val = 'Store Demand Forecast';
			break;
		case 701:
		case 702:
			val = 'eComm Sales';
			break;
		case 801:
		case 802:
		case 901:
		case 951:
		case 952:
			val = 'Vendor Scorecard';
			break;
		case 1001:
		case 1002:
		case 1003:
			val = 'FY24 Vendor Scorecard';
			break;
		case 1004:
		case 1005:
		case 1006:
			val = 'FY25 Vendor Scorecard';
			break;
		case 1101:
		case 1102:
		case 1201:
		case 1151:
		case 1152:
			val = 'Future Valid Item/Store';
			break;
		case 1301:
		case 1302:
		case 1351:
		case 1352:
		case 1401:
			val = 'Tender Analysis';
			break;
		case 1701:
		case 1702:
		case 1801:
		case 1751:
		case 1752:
			val = 'eComm Instock';
			break;
		case 1501:
		case 1502:
		case 1551:
			val = 'Bottle Deposit';
			break;
		case 1703:
		case 1704:
		case 1753:
		case 1754:
		case 1802:
			val = 'Quick Item Information';
			break;
		case 615:
		case 616:
		case 415:
		case 416:
		case 515:
			val = 'eComm Inventory';
			break;
		case 421:
		case 422:
		case 521:
			val = 'Register Sales';
			break;
		case 417:
		case 418:
		case 517:
		case 617:
		case 618:
			val = 'Store Markup and Markdowns';
			break;
		case 419:
		case 619:
		case 519:
			val = 'Hourly Store Inventory';
			break;
		case 423:
		case 424:
		case 524:
			val = 'Historical Report - Omni';
			break;
		default:
			val = 'Omni Sales';
	}
	return val;
};

export const isCustomDataset = (selectedTemplate) => {
	return selectedTemplate === CUSTOM_DATASET_ID || isNaN(Number(selectedTemplate));
};

export const isUnsavedCustomDataset = (selectedTemplate) => {
	return selectedTemplate === CUSTOM_DATASET_ID;
};

export const isCustomDatasetModular = (selectedTables) => {
	return selectedTables.every((table) => table?.dataSet?.includes('Mod'));
};

export const formatCustomAttrColId = (businessName) => {
	return businessName?.replace(/ /g, '_');
};

export const getSavedCustomDatasetWithAttributes = (
	savedCustomDatasets,
	customDatasetDetails,
	customAttribute
) => {
	const savedCustomDatasetsWithTime = savedCustomDatasets.map((dataset) => {
		const newSavedCustomDataset = {
			...dataset,
			customAttribute: customAttribute,
			filter: customDatasetDetails?.filter,
			filterWeekly: customDatasetDetails?.filterWeekly,
			filterWeeklyMultiWindow: customDatasetDetails?.filterWeeklyMultiWindow,
			defaultFilters: customDatasetDetails?.defaultFilters
		};
		const tables = dataset?.tables ?? [];
		let measures = [];
		let dimensions = [];

		const fullTables = customDatasetDetails?.tablesInfo.filter((tableInfo) => {
			return tables?.includes(tableInfo.tableName);
		});

		// If it is not mod only, then add time
		if (!isCustomDatasetModular(fullTables)) {
			newSavedCustomDataset.time = customDatasetDetails.time;
		}
		tables.forEach((tableName) => {
			const table = customDatasetDetails.tablesInfo.find(
				(tableInfo) => tableInfo.tableName === tableName
			);
			if (table?.measures?.length > 0) {
				measures = [...measures, ...table?.measures];
			}
			if (table?.dimensions?.length > 0) {
				dimensions = [...dimensions, ...table?.dimensions];
			}
		});
		if (measures.length > 0) {
			newSavedCustomDataset.measures = measures;
		}
		if (dimensions.length > 0) {
			newSavedCustomDataset.dimensions = dimensions;
		}
		return newSavedCustomDataset;
	});

	return savedCustomDatasetsWithTime;
};

export const getResultObject = (id, dataset, selectedTables) => {
	return {
		sortOrder: 1,
		folderNames: [...dataset.tableNames],
		templateId: id,
		dimensions: [],
		measures: [],
		filter: dataset.filter,
		filterWeekly: dataset.filterWeekly,
		filterWeeklyMultiWindow: dataset.filterWeeklyMultiWindow,
		view: dataset?.view ?? ABC,
		defaultFilters: dataset?.defaultFilters,
		tables: selectedTables
	};
};

export const assignDimensionsAndMeasures = (baseObj, tableObj = []) => {
	tableObj.forEach((selectedTable) => {
		if (selectedTable.dimensions) {
			baseObj.dimensions = [...baseObj.dimensions, ...selectedTable.dimensions];
		}
		if (selectedTable.measures) {
			baseObj.measures = [...baseObj.measures, ...selectedTable.measures];
		}
	});
	return baseObj;
};

export const isBasicPersona = (customerType) => {
	return customerType === BASIC;
};

export const computeIncompatibilityObject = (value, currentIndex, templateName, metricCalcAttr) => {
	const _metricCal = deepClone(metricCalcAttr ?? INITIAL_SSI_COMPATIBILITY);
	if (STR_TMPT_LIST.includes(Number(templateName)) || isCustomDataset(templateName)) {
		if (value.dataSet) {
			value.dataSet.forEach((el) => {
				if (!_metricCal?.dataSet[el]) {
					_metricCal.dataSet[el] = 0;
				}
				if (currentIndex === -1) {
					_metricCal.dataSet[el]++;
				} else {
					if (_metricCal?.dataSet[el] > 0) {
						_metricCal.dataSet[el]--;
					}
					if (_metricCal?.dataSet[el] === 0) {
						delete _metricCal.dataSet[el];
					}
				}
			});
		}

		if (value.incompatible) {
			value.incompatible.forEach((el) => {
				if (!_metricCal?.incompatibility[el]) {
					_metricCal.incompatibility[el] = 0;
				}
				if (currentIndex === -1) {
					_metricCal.incompatibility[el]++;
				} else {
					if (_metricCal?.incompatibility[el] > 0) {
						_metricCal.incompatibility[el]--;
					}
					if (_metricCal?.incompatibility[el] === 0) {
						delete _metricCal.incompatibility[el];
					}
				}
			});
			// If we added either an incompatible column (or) filter
			if (currentIndex === -1) {
				// Create a key-value pair for the newly added column/filter if not done already
				if (!_metricCal?.incompatibilityAttrCount[value.businessName]) {
					_metricCal.incompatibilityAttrCount[value.businessName] = 0;
				}
				// Add the new column/filter to the incompatibility list only if it has not been added already
				if (!_metricCal.incompatibilityAttr.includes(value.businessName)) {
					_metricCal.incompatibilityAttr.push(value.businessName);
				}
				// Increment the key-value pair associated with it.
				// Max value is 2 which indicates both column and filter have been added
				// 1 would indicate that either the column or filter has been added for that field
				_metricCal.incompatibilityAttrCount[value.businessName]++;
			} else {
				// If we remove either an incompatible column (or) filter.

				// a value greater than 1 indicates that both the column and filter have been added and decrementing
				// by 1 indicates either the column/filter have been removed but not both.
				if (_metricCal.incompatibilityAttrCount[value.businessName] > 1) {
					_metricCal.incompatibilityAttrCount[value.businessName]--;
				} else {
					// If value is less than or equal to 1 would mean only the column or filter is left over and we
					// need to remove both the attribute and the count from the object.
					_metricCal.incompatibilityAttr = _metricCal?.incompatibilityAttr.filter(
						(el) => el !== value.businessName
					);
					delete _metricCal.incompatibilityAttrCount[value.businessName];
				}
			}
		}
	}

	return _metricCal;
};

/**
 * For a specific filter, the function checks if it has one of those three filters selected.
 * @param calFilter Calendar filter - could be Date, week, month, quarter or year
 * @returns whether the user has selected either Last 104 weeks (or) Last 12 months (or) Last 4 quarters (or) WMT Last Year
 */
export const determine104WeeksFeatureInFilter = (calFilter) => {
	const filterCondition = calFilter?.filter?.condition;
	const is104WeeksSelected = filterCondition?.includes(wmtLastOneHundredAndFourWeeks);
	const is12MonthsSelected = filterCondition?.includes('Last 12 months');
	const is4QuartersSelected = filterCondition?.includes('WMT Last 4 quarters');
	const isWMTLastYearSelected = filterCondition?.includes(FILTER_CONDITION_LABELS.WMT_LAST_YEAR);
	let is104WeeksFeatureSelected =
		is104WeeksSelected || is12MonthsSelected || is4QuartersSelected || isWMTLastYearSelected;

	if (calFilter.businessName === WEEK_KEY && filterCondition === CUSTOM) {
		const hasUserSelectedOver52Weeks = moment(calFilter?.filter?.value[0]).isBefore(
			moment().clone().subtract(IS_BEFORE_52_WEEKS, 'week').endOf('week')
		);
		if (hasUserSelectedOver52Weeks) {
			is104WeeksFeatureSelected = true;
		}
	}

	return is104WeeksFeatureSelected;
};

/**
 * Using the filterList's current state, the function checks whether the filterList has a calendar filter
 * which has one of those three (Last 104 weeks, Last 12 months, WMT Last 4 quarters) filters selected.
 * @param filterList Filter List's current state - has all the filters selected by the user.
 * @returns whether the user has selected either Last 104 weeks (or) Last 12 months (or) Last 4 quarters (or) WMT Last Year
 */
export const determine104WeeksFeatureInFilterList = (filterList) => {
	let is104FeatureSelected = false;
	const weekFilter = filterList?.find((filter) => filter?.businessName === WEEK_KEY);
	const monthFilter = filterList?.find((filter) => filter?.businessName === MONTH_KEY);
	const quarterFilter = filterList?.find((filter) => filter?.businessName === QUARTER_KEY);
	const yearFilter = filterList?.find((filter) => filter?.businessName === YEAR_KEY);

	if (weekFilter) {
		is104FeatureSelected = weekFilter.filter?.condition?.includes(wmtLastOneHundredAndFourWeeks);
		if (weekFilter.filter?.condition === CUSTOM) {
			const hasUserSelectedOver52Weeks = moment(weekFilter.filter?.value[0]).isBefore(
				moment().clone().subtract(IS_BEFORE_52_WEEKS, 'week').endOf('week')
			);
			if (hasUserSelectedOver52Weeks) {
				is104FeatureSelected = true;
			}
		}
	}
	if (monthFilter) {
		is104FeatureSelected =
			is104FeatureSelected || monthFilter.filter?.condition?.includes('Last 12 months');
	}
	if (quarterFilter) {
		is104FeatureSelected =
			is104FeatureSelected || quarterFilter.filter?.condition?.includes('WMT Last 4 quarters');
	}
	if (yearFilter) {
		is104FeatureSelected =
			is104FeatureSelected ||
			yearFilter.filter?.condition?.includes(FILTER_CONDITION_LABELS.WMT_LAST_YEAR);
	}

	return is104FeatureSelected;
};

export const remove104WeeksOptions = (o) => {
	o.Week = o.Week.filter((el) => el.value !== wmtLastOneHundredAndFourWeeks);
	o.Month = o.Month.filter((el) => el.value !== 'Last 12 months');
	o.Quarter = o.Quarter.filter((el) => el.value !== `WMT Last 4 quarters`);
	o.Year = o.Year.filter((el) => el.value !== FILTER_CONDITION_LABELS.WMT_LAST_YEAR);
};

const CURR_YR = moment().year();
const getWalmartYearTodate = () => {
	return {
		[`${CURR_YR} (${WALMART_CALENDER_KEY} ${YEAR_TO_DATE_KEY})`]: FUZZY_OPTIONS[YEAR_TO_DATE_KEY]
	};
};

const getGregorianYearToDate = () => {
	return { [`${CURR_YR} (${YEAR_TO_DATE_KEY})`]: FUZZY_OPTIONS[YEAR_TO_DATE_KEY] };
};

export const getFuzzyOptions = () => {
	return {
		...FUZZY_OPTIONS,
		...getWalmartYearTodate(),
		...getGregorianYearToDate()
	};
};

/**
 * Filters the filterList by removing unselected items based on unique folder names.
 *
 * @param {Array} filterList - The current filter list.
 * @param {Array} selectedList - The list of selected items.
 * @returns {Array} - The updated filter list.
 */
export const filterUnselectedItems = (filterList, selectedList, exemptList) => {
	// Extract unique folder names from the selected list
	const uniqueFolderNames = new Set(selectedList.map((item) => item.folderName));

	// Filter the filterList based on the unique folder names and businessName
	return filterList.filter((item) => {
		// Always include items with businessName 'Date'
		if (item.businessName === 'Date' || !exemptList.includes(item.businessName)) {
			return true;
		}
		// Include items whose folderName exists in uniqueFolderNames
		return uniqueFolderNames.has(item.folderName);
	});
};

/**
 * Checks if any of the daily grain options are selected in the MTS filter.
 * @param {Object} mtsTimeFilter MTS filter object
 * @returns {boolean} true if any of the daily grain options are selected, false otherwise
 */
export const checkIfDayGrainIsSelectedMTS = (mtsTimeFilter) => {
	if (!(Object.keys(mtsTimeFilter?.mts ?? {}).length > 0)) {
		return false;
	}
	// Daily grain options in MTS 2.0
	const dailyGrainMTSOptions = [
		LAST_WEEKEND_KEY,
		FILTER_CONDITION_LABELS.YESTERDAY,
		FILTER_CONDITION_LABELS.WMT_WEEK_TO_DATE,
		FILTER_CONDITION_LABELS.WMT_MONTH_TO_DATE,
		FILTER_CONDITION_LABELS.WMT_YEAR_TO_DATE
	];
	for (const key in mtsTimeFilter.mts) {
		if ([CUSTOM_DATE_1, CUSTOM_DATE_2].includes(key)) {
			if (mtsTimeFilter.mts[key]?.rangeType === DAY_KEY_LOWERCASE) {
				return true;
			}
		}
		if (dailyGrainMTSOptions.includes(key)) {
			return true;
		}
	}
	return false;
};
